import { useCallback, useEffect, useRef, useState } from "react";
import debounce from "lodash/debounce";
import { useAppStore } from "@/store/app/slice";
import { InspectorsRepository } from "@/api/inspectors/repository";
import { InspectorsService } from "@/api/inspectors/service";
import type { IInspector } from "@/types/users-inspectors";
import { serverErrorHandler } from "@/utils/serverErrorHandler";

export const useFetchInspectorProfile = () => {
  const abortController = useRef<AbortController | null>(null);
  const user = useAppStore((s) => s.user);
  const [profile, setProfile] = useState<IInspector | null>(null);

  const fetchProfile = useCallback(() => {
    if (!user) {
      return;
    }

    const inspectorsRepository = new InspectorsRepository(
      new InspectorsService(),
    );

    abortController.current?.abort();
    abortController.current = new AbortController();
    const signal = abortController.current.signal;

    inspectorsRepository
      .getProfileByUserId(
        {
          userId: user.id,
        },
        { signal },
      )
      .then((response) => {
        setProfile(response);
      })
      .catch(serverErrorHandler());
  }, [user]);

  useEffect(() => {
    const debouncedFetch = debounce(fetchProfile, 300);
    debouncedFetch();

    return () => {
      debouncedFetch.cancel();
      abortController.current?.abort();
    };
  }, [fetchProfile]);

  return {
    fetchProfile,
    profile,
  };
};
